/**
 * loadDetailsExportMapper.ts
 *
 * Maps API rows (`ASNLoadDetailRow`, as returned by
 * `POST /api/asn/load-details/list`) into the flat array-of-cells shape the
 * Load Details Excel export writes one worksheet row from — one cell per
 * `LoadDetailsColumnDef`, in the exact same order as the on-screen table's
 * columns (`LOAD_DETAILS_COLUMNS`), so the Excel headers always line up with
 * the exported values below them.
 */

import { ASNLoadDetailRow } from '../api/asnOverviewKpiApi';
import { LOAD_DETAILS_COLUMNS, LoadDetailsColumnDef } from '../constants/loadDetailsColumns';

/**
 * Maps a single `ASNLoadDetailRow` into an ordered array of Excel cell
 * values — one per column in `columns` (defaults to the table's own column
 * set/order). Missing values are blank (`''`) — never "undefined"/"null"/
 * "NaN", never guessed or substituted from another field. Numeric columns
 * (SKU Count, Cases) are written as real numbers (not formatted strings) so
 * Excel treats them as numeric data, unless the value is missing/non-finite,
 * in which case the cell is left blank.
 */
export const mapApiRowToTableRow = (
  row: ASNLoadDetailRow,
  columns: LoadDetailsColumnDef[] = LOAD_DETAILS_COLUMNS,
): Array<string | number> =>
  columns.map((col) => {
    const raw = row[col.field];
    if (raw === undefined || raw === null) return '';
    if (col.numeric) {
      const num = typeof raw === 'number' ? raw : Number(raw);
      return Number.isFinite(num) ? num : '';
    }
    return String(raw).trim();
  });

export default mapApiRowToTableRow;
